"use client";

import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import { VariantProps, cva } from "class-variance-authority";
import { ArrowDownIcon, CloseIcon } from "icons";
import React, { Ref, useState } from "react";
import { Button, Group, Label, ComboBoxProps } from "react-aria-components";

import { replaceSpecialCharacters } from "../../utils/string-functions";
import { Text } from "../Text/Text";

export interface ISearchDropdownOptions {
  id: string | number;
  name: string;
}
export interface ISearchDropdownProps<T extends object>
  extends ComboBoxProps<T>,
    VariantProps<typeof SelectButtonClasses> {
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  helperMessage?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isLocation?: boolean;
  hasValue?: boolean;
  options?: ISearchDropdownOptions[];
  onClearButtonPress?: () => void;
  inputReference?: Ref<HTMLInputElement> | undefined;
  onInputChange?: (value: string) => void;
  inputText?: string;
  defaultValue?: string;
}

function ComboBoxClearButton({
  onClearPress,
}: Readonly<{
  onClearPress?: () => void;
}>): JSX.Element {
  return (
    <Button
      slot={null}
      className="clear-button outline-none visible"
      aria-label="Clear"
      onPress={onClearPress}
    >
      <CloseIcon className="w-2.5 h-2.5" />
    </Button>
  );
}

export function SearchDropdownAlt<T extends object>({
  label,
  placeholder,
  errorMessage = "",
  options,
  intent,
  isDisabled = false,
  isRequired = false,
  hasValue = false,
  size,
  onClearButtonPress,
  inputReference,
  onSelectionChange,
  onInputChange,
  inputText,
  defaultValue,
}: Readonly<ISearchDropdownProps<T>>): JSX.Element {
  let calculatedIntent: "primary" | "success" | "warning" | "danger";

  if (intent) {
    calculatedIntent = intent;
  } else if (errorMessage) {
    calculatedIntent = "danger";
  } else if (hasValue) {
    calculatedIntent = "success";
  } else {
    calculatedIntent = "primary";
  }

  const [internalSelectedItem, setInternalSelectedItem] = useState<string>(
    defaultValue || ""
  );
  const selectedItem =
    inputText !== undefined ? inputText : internalSelectedItem;

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (value === undefined) {
      setInternalSelectedItem(value);
    } else {
      setInternalSelectedItem(value);
    }
    setIsPopoverOpen(!!value);
    onInputChange?.(value);
  };

  const handleSelect = (value: string): void => {
    if (value === undefined) {
      setInternalSelectedItem(value);
    }
    onSelectionChange?.(value);
    setIsPopoverOpen(false);
  };

  const handleClear = (): void => {
    if (inputText === undefined) {
      setInternalSelectedItem("");
    }
    setIsPopoverOpen(false);
    onClearButtonPress?.();
    onInputChange?.("");
    onSelectionChange?.("");
  };

  return (
    <Combobox
      onSelect={handleSelect}
      aria-labelledby="demo"
      className={SelectDropdownWrapper({ size })}
      openOnFocus
    >
      <Label>
        <Text variant="small">
          {label}
          {isRequired && (
            <Text variant="small" intent="secondary" className="ml-1">
              (required)
            </Text>
          )}
        </Text>
        <Group
          isDisabled={isDisabled}
          className={SelectButtonClasses({ intent: calculatedIntent, size })}
        >
          <ComboboxInput
            onChange={handleInput}
            placeholder={placeholder}
            disabled={isDisabled}
            ref={inputReference}
            value={selectedItem}
            className="outline-none  data-[disabled]:bg-gray-200 appearance-none w-full text-sm placeholder-gray-400 truncate mr-4"
          />
          <div className="flex items-center justify-center gap-x-2">
            {!isDisabled && selectedItem && (
              <>
                <ComboBoxClearButton
                  onClearPress={() => {
                    handleClear();
                  }}
                />
                <Button
                  className={options && options.length > 0 ? "" : "hidden"}
                  isDisabled={isDisabled}
                  onPress={() => setIsPopoverOpen(!isPopoverOpen)}
                >
                  <ArrowDownIcon className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
        </Group>
      </Label>
      {errorMessage && (
        <Text variant="small" intent="danger">
          {errorMessage}
        </Text>
      )}
      {isPopoverOpen && options && options?.length > 0 && (
        <ComboboxPopover className={selectPopoverClasses()}>
          <div className="h-0.5 bg-black mx-2 rounded" />
          <ComboboxList className={SelectListClasses()}>
            {options.map((item) => (
              <ComboboxOption
                key={item.id.toString()}
                value={replaceSpecialCharacters(item.name)}
                className={SelectListItemClasses()}
              />
            ))}
          </ComboboxList>
        </ComboboxPopover>
      )}
    </Combobox>
  );
}

const SelectDropdownWrapper = cva(" appearance-none flex flex-col gap-1", {
  variants: {
    size: {
      xs: "w-20",
      sm: "w-[155px]",
      md: "w-60",
      lg: "w-72",
      xl: "w-80",
      full: "w-full flex-1",
    },
  },
  defaultVariants: {
    size: "md",
  },
});
const SelectButtonClasses = cva(
  "flex items-center data-[disabled]:bg-gray-200 justify-between border p-2 border-1 rounded-md h-10 hover:border-primary focus-within:border-primary",
  {
    variants: {
      intent: {
        primary: "border-background-dark",
        success: "border-success",
        warning: "border-warning",
        danger: "border-danger",
      },
      isOpen: {
        true: "border-b-0 rounded-b-none",
      },
      size: {
        xs: "w-20",
        sm: "w-[155px]",
        md: "w-60",
        lg: "w-72",
        xl: "w-80",
        full: "w-full",
      },
    },
    defaultVariants: {
      intent: "primary",
      size: "md",
    },
  }
);
const selectPopoverClasses = cva(
  "border bg-white z-50 shadow border-background-dark rounded border-t-0 rounded-t-none entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out",
  {
    variants: {
      size: {
        xs: "w-20",
        sm: "w-[155px]",
        md: "w-60",
        lg: "w-72",
        xl: "w-80",
        full: "w-full max-w-[--trigger-width]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);
const SelectListClasses = cva(
  "flex flex-col overflow-auto overflow-x-hidden max-h-[300px] flex-1 w-full outline-none "
);
const SelectListItemClasses = cva(
  "outline-none hover:bg-primaryLight p-1 cursor-pointer text-sm data-[highlighted]:bg-primaryLight focus:bg-primaryLight list-none",
  {
    variants: {
      selected: {
        true: "bg-primary",
      },
    },
    defaultVariants: {
      selected: false,
    },
  }
);
